
import { useState, useEffect } from 'react';

interface SlideData {
  image: string;
  title: string;
  subtitle: string;
}

interface SlideshowProps {
  slides: SlideData[];
  autoPlay?: boolean;
  interval?: number;
}

export default function Slideshow({ slides, autoPlay = true, interval = 5000 }: SlideshowProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!autoPlay) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, interval);
    return () => clearInterval(timer);
  }, [autoPlay, interval, slides.length]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (lightboxIndex !== null) {
        if (e.key === 'ArrowRight') setLightboxIndex((prev) => prev !== null ? (prev + 1) % slides.length : 0);
        if (e.key === 'ArrowLeft') setLightboxIndex((prev) => prev !== null ? (prev - 1 + slides.length) % slides.length : 0);
        if (e.key === 'Escape') setLightboxIndex(null);
      } else if (galleryOpen && e.key === 'Escape') {
        setGalleryOpen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [galleryOpen, lightboxIndex, slides.length]);

  useEffect(() => {
    document.body.style.overflow = (galleryOpen || lightboxIndex !== null) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [galleryOpen, lightboxIndex]);

  const goToSlide = (index: number) => setCurrentSlide(index);
  const nextSlide = (e: React.MouseEvent) => { e.stopPropagation(); setCurrentSlide((prev) => (prev + 1) % slides.length); };
  const prevSlide = (e: React.MouseEvent) => { e.stopPropagation(); setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length); };

  return (
    <>
      {/* Slideshow */}
      <div
        className="relative w-full h-96 md:h-[500px] lg:h-[600px] overflow-hidden rounded-xl shadow-2xl cursor-pointer group"
        onClick={() => setGalleryOpen(true)}
        title="Click to view gallery"
      >
        <div className="relative w-full h-full">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
            >
              <img src={slide.image} alt={slide.title} className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="text-center text-white px-6">
                  <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-4">{slide.title}</h2>
                  <p className="text-lg md:text-xl lg:text-2xl font-light">{slide.subtitle}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Gallery hint overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center pointer-events-none">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/60 text-white px-5 py-2.5 rounded-full flex items-center gap-2 text-sm font-medium">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-gallery-line text-base"></i>
            </div>
            View Gallery
          </div>
        </div>

        {/* Navigation Arrows */}
        <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-200 cursor-pointer z-10">
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-200 cursor-pointer z-10">
          <i className="ri-arrow-right-line text-xl"></i>
        </button>

        {/* Dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
              className={`w-3 h-3 rounded-full transition-all duration-200 cursor-pointer ${index === currentSlide ? 'bg-white' : 'bg-white/50 hover:bg-white/70'}`}
            />
          ))}
        </div>
      </div>

      {/* Gallery Modal */}
      {galleryOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" onClick={() => setGalleryOpen(false)}>
          <div className="flex items-center justify-between px-6 py-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white text-xl font-semibold">Gallery</h3>
            <button
              onClick={() => setGalleryOpen(false)}
              className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div
            className="flex-1 overflow-y-auto px-6 pb-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className="relative group cursor-pointer rounded-xl overflow-hidden aspect-video shadow-lg"
                  onClick={() => { setLightboxIndex(index); setGalleryOpen(false); }}
                >
                  <img src={slide.image} alt={slide.title} className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end">
                    <div className="p-3 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <p className="text-white text-sm font-semibold leading-tight">{slide.title}</p>
                      <p className="text-white/80 text-xs mt-0.5">{slide.subtitle}</p>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-zoom-in-line text-white text-sm"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" onClick={() => setLightboxIndex(null)}>
          <div className="flex items-center justify-between px-6 py-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => { setLightboxIndex(null); setGalleryOpen(true); }}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors cursor-pointer text-sm"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              Back to Gallery
            </button>
            <span className="text-white/50 text-sm">{lightboxIndex + 1} / {slides.length}</span>
            <button
              onClick={() => setLightboxIndex(null)}
              className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center relative px-16" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightboxIndex((prev) => prev !== null ? (prev - 1 + slides.length) % slides.length : 0)}
              className="absolute left-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>

            <div className="max-w-4xl w-full">
              <img
                src={slides[lightboxIndex].image}
                alt={slides[lightboxIndex].title}
                className="w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
              />
              <div className="text-center mt-4">
                <p className="text-white text-lg font-semibold">{slides[lightboxIndex].title}</p>
                <p className="text-white/60 text-sm mt-1">{slides[lightboxIndex].subtitle}</p>
              </div>
            </div>

            <button
              onClick={() => setLightboxIndex((prev) => prev !== null ? (prev + 1) % slides.length : 0)}
              className="absolute right-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>

          {/* Thumbnail strip */}
          <div className="flex justify-center gap-2 px-6 py-4 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
            {slides.map((slide, index) => (
              <button
                key={index}
                onClick={() => setLightboxIndex(index)}
                className={`flex-shrink-0 w-16 h-12 rounded-md overflow-hidden transition-all cursor-pointer ${index === lightboxIndex ? 'ring-2 ring-white opacity-100' : 'opacity-50 hover:opacity-80'}`}
              >
                <img src={slide.image} alt={slide.title} className="w-full h-full object-cover object-center" />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
